import React, { useMemo } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { DashboardAssessmentData } from '../../utils/dashboardAssessmentAdapter';
import ContentLoader from '../../components/ContentLoader';
import { CLIENT_CHART_THEME as T } from '../../constants/clientChartTheme';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface RadarChartProps {
  assessmentData: DashboardAssessmentData | null;
  loading?: boolean;
  error?: string | null;
}

const RadarChart: React.FC<RadarChartProps> = ({
  assessmentData,
  loading = false,
  error = null,
}) => {
  const graphPoints = assessmentData?.graphPoints ?? [];
  const maxRating = assessmentData?.maxRating ?? 5;

  const chartData = useMemo(
    () => ({
      labels: graphPoints.map((point) => point.capabilityName),
      datasets: [
        {
          label: 'Current Status',
          data: graphPoints.map((point) => point.currentAverage),
          backgroundColor: T.primaryFill,
          borderColor: T.primary,
          borderWidth: 2,
          pointBackgroundColor: T.primary,
          pointBorderColor: '#ffffff',
          pointHoverBackgroundColor: '#ffffff',
          pointHoverBorderColor: T.primary,
        },
        {
          label: 'Future Status',
          data: graphPoints.map((point) => point.futureAverage),
          backgroundColor: T.accentFill,
          borderColor: T.accent,
          borderWidth: 2,
          pointBackgroundColor: T.accent,
          pointBorderColor: '#ffffff',
          pointHoverBackgroundColor: '#ffffff',
          pointHoverBorderColor: T.accent,
        },
      ],
    }),
    [graphPoints]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            color: T.text,
            font: {
              family: T.fontFamily,
              size: 12,
              weight: '500' as const,
            },
            padding: 16,
            usePointStyle: true,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: T.tooltipBg,
          titleColor: T.tooltipTitle,
          bodyColor: T.tooltipBody,
          borderColor: T.tooltipBorder,
          borderWidth: 1,
          padding: 12,
          callbacks: {
            title(tooltipItems: Array<{ label?: string }>) {
              return `Capability: ${tooltipItems[0]?.label ?? ''}`;
            },
            label(tooltipItem: { raw?: unknown }) {
              return `Score: ${tooltipItem.raw}`;
            },
          },
        },
      },
      scales: {
        r: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            color: T.textMuted,
            backdropColor: 'transparent',
            showLabelBackdrop: false,
            callback(value: number | string) {
              return typeof value === 'number' && value % 1 === 0 ? value : '';
            },
          },
          max: maxRating,
          grid: {
            color: T.grid,
          },
          angleLines: {
            color: T.grid,
          },
          pointLabels: {
            color: T.text,
            callback(label: string) {
              return label.split(' ');
            },
            font: {
              family: T.fontFamily,
              size: 12,
              weight: '600' as const,
            },
          },
        },
      },
    }),
    [maxRating]
  );

  if (loading) {
    return <ContentLoader compact label="Loading project progress chart…" />;
  }

  if (error) {
    return <p className="dash-chart-error">{error}</p>;
  }

  if (graphPoints.length === 0) {
    return (
      <p className="dash-chart-status">
        Complete assessment ratings to see your project progress chart.
      </p>
    );
  }

  return (
    <>
      <div className="dash-project-progress-parent">
        <h2 className="dash-your-project app-heading-accent">Project progress</h2>
      </div>
      <div className="dash-frame-parent3">
        <div
          className="dash-mainchart-parent"
          style={{
            marginTop: '-8px',
          }}
        >
          <Radar height={500} width={500} data={chartData} options={options} />
        </div>
        <div
          className="dash-legends-wrapper"
          style={{
            marginBottom: '40px',
          }}
        >
          <div className="dash-legends1" />
        </div>
      </div>
    </>
  );
};

export default RadarChart;
